import {
  normalizeCommon,
  validateCommon,
  type NdtDocumentCommon,
  type NdtPhoto,
  type NdtReportCommon,
  type NdtSignatory,
  type NdtValidationResult
} from "./ndt-report-common.js";
import { formatDecimalBR, parseDecimalBR } from "../engineering/me-measurements.js";

/**
 * RELATÓRIO DE ENSAIO ULTRASSÔNICO B-SCAN / C-SCAN.
 *
 * Domínio PRÓPRIO, e não uma variação do ME:
 *
 *   ME convencional   pontos isolados de espessura.
 *   B-Scan / C-Scan   PERFIL / MAPEAMENTO de uma região varrida.
 *
 * A unidade de trabalho é a ÁREA ENSAIADA (`BscanArea`). Resultado, foto e
 * imagem técnica apontam para ela pelo identificador (`scanAreaKey`), nunca
 * pelo texto — é o que dá rastreabilidade real entre "BS09-61" na tabela de
 * resultados, a foto do local e o print do aparelho.
 *
 *     BscanReport
 *       └─ BscanArea ─┬─ BscanResult
 *                     ├─ BscanPhoto
 *                     └─ BscanImage
 *
 * O que é igual aos demais END (contexto, procedimento, norma, critério,
 * signatários, trilha, snapshot, fluxo do PLH) vem de `NdtReportCommon` —
 * duplicar isso criaria um segundo fluxo de aprovação, que o comando proíbe.
 *
 * VALORES NUMÉRICOS SÃO NÚMERO. "6,90 mm" é apresentação; o domínio guarda
 * 6.9 e a unidade é sempre milímetro. Campo vazio é `null`, nunca 0.
 */

// ─────────────────────────────────────────────────────────────── laudo

/**
 * Laudo do B-Scan. Estende o do END comum com ADPC ("a definir pelo critério
 * do cliente"), que o ensaio de mapeamento precisa e os demais métodos não
 * usam — por isso é tipo próprio, e o `NdtLaudo` compartilhado não foi
 * alterado.
 */
export type BscanLaudo = "APROVADO" | "REPROVADO" | "EXAME_COMPLEMENTAR" | "ADPC" | "";

export const BSCAN_LAUDO_OPTIONS: ReadonlyArray<{ value: BscanLaudo; label: string; abbreviation: string }> = [
  { value: "", label: "Selecione", abbreviation: "" },
  { value: "APROVADO", label: "Aprovado", abbreviation: "A" },
  { value: "REPROVADO", label: "Reprovado", abbreviation: "R" },
  { value: "EXAME_COMPLEMENTAR", label: "Exame complementar recomendado", abbreviation: "REC" },
  { value: "ADPC", label: "A definir pelo critério do cliente", abbreviation: "ADPC" }
];

export function bscanLaudoLabel(laudo: BscanLaudo | null | undefined): string {
  return BSCAN_LAUDO_OPTIONS.find(o => o.value === laudo)?.label ?? "";
}

/** A abreviação do PDF. O domínio guarda o significado, não a sigla. */
export function bscanLaudoAbbreviation(laudo: BscanLaudo | null | undefined): string {
  return BSCAN_LAUDO_OPTIONS.find(o => o.value === laudo)?.abbreviation ?? "";
}

export const BSCAN_LEGEND = "A = Aprovado    R = Reprovado    REC = Exame complementar recomendado    ADPC = A definir pelo critério do cliente    Unidade = mm";

// ───────────────────────────────────────────────────────── enumerações

export type BscanScanMode = "B_SCAN" | "C_SCAN" | "B_C_SCAN" | "";

export const BSCAN_SCAN_MODE_OPTIONS: ReadonlyArray<{ value: BscanScanMode; label: string }> = [
  { value: "", label: "Selecione" },
  { value: "B_SCAN", label: "B-Scan" },
  { value: "C_SCAN", label: "C-Scan" },
  { value: "B_C_SCAN", label: "B-Scan / C-Scan" }
];

export function bscanScanModeLabel(mode: BscanScanMode | null | undefined): string {
  return BSCAN_SCAN_MODE_OPTIONS.find(o => o.value === mode && mode !== "")?.label ?? "";
}

export type BscanPhotoKind = "VISTA_GERAL" | "LOCAL_VARREDURA" | "DETALHE" | "OUTRO";

export const BSCAN_PHOTO_KIND_OPTIONS: ReadonlyArray<{ value: BscanPhotoKind; label: string }> = [
  { value: "VISTA_GERAL", label: "Vista geral do sistema" },
  { value: "LOCAL_VARREDURA", label: "Local da varredura" },
  { value: "DETALHE", label: "Detalhe" },
  { value: "OUTRO", label: "Outro" }
];

export function bscanPhotoKindLabel(kind: BscanPhotoKind | null | undefined): string {
  return BSCAN_PHOTO_KIND_OPTIONS.find(o => o.value === kind)?.label ?? "";
}

export type BscanImageKind = "B_SCAN" | "C_SCAN" | "A_SCAN" | "OUTRO";

export const BSCAN_IMAGE_KIND_OPTIONS: ReadonlyArray<{ value: BscanImageKind; label: string }> = [
  { value: "B_SCAN", label: "B-Scan" },
  { value: "C_SCAN", label: "C-Scan" },
  { value: "A_SCAN", label: "A-Scan" },
  { value: "OUTRO", label: "Outro registro técnico" }
];

export function bscanImageKindLabel(kind: BscanImageKind | null | undefined): string {
  return BSCAN_IMAGE_KIND_OPTIONS.find(o => o.value === kind)?.label ?? "";
}

export const BSCAN_DEFAULT_OBJECTIVE =
  "Realizar mapeamento de corrosão por ultrassom B-Scan / C-Scan para caracterização do perfil de espessuras e identificação da menor espessura remanescente nas regiões selecionadas.";

// ─────────────────────────────────────────────────────────── entidades

/** Área ensaiada — a unidade de trabalho do mapeamento. */
export interface BscanArea {
  /** Identificador estável da área (`scanAreaId`). Resultado, foto e imagem apontam para ele. */
  key: string;
  item: string;
  /** Código da área / registro do aparelho ("BS09-61"). */
  code: string;
  identification: string;
  scanFile: string;
  location: string;
  /** Extensão varrida no eixo X, em mm. */
  lengthXMm: number | null;
  /** Extensão varrida no eixo Y, em mm (ausente em varredura linear). */
  lengthYMm: number | null;
  orientation: string;
  nominalThicknessMm: number | null;
  minDesignThicknessMm: number | null;
  observation: string;
}

/** Resultado de uma área. O vínculo é o `scanAreaKey`, não o texto da área. */
export interface BscanResult {
  key: string;
  scanAreaKey: string;
  item: string;
  minimumThicknessMm: number | null;
  laudo: BscanLaudo;
  observation: string;
}

export interface BscanPhoto extends NdtPhoto {
  scanAreaKey: string | null;
  kind: BscanPhotoKind;
  caption: string;
  order: number;
}

export interface BscanImage {
  key: string;
  scanAreaKey: string | null;
  kind: BscanImageKind;
  image: string;
  fileName: string;
  fileSize: number;
  uploadedAt: string;
  caption: string;
  associatedThicknessMm: number | null;
  observation: string;
  order: number;
}

/**
 * Marcação de uma área sobre o isométrico. Coordenadas relativas (0..1) ao
 * desenho, para valerem em qualquer tamanho de exibição. O desenho ORIGINAL
 * nunca é alterado: a marcação é uma camada separada.
 */
export interface BscanDrawingMarker {
  key: string;
  scanAreaKey: string;
  x: number;
  y: number;
}

export interface BscanReport extends NdtReportCommon<BscanDocumentModel> {
  photos: BscanPhoto[];

  // 01 — IDENTIFICAÇÃO (complementos)
  system: string;
  inspectionLocation: string;
  modality: string;

  // 02 — OBJETIVO
  objective: string;

  // 03 — DADOS TÉCNICOS DO ENSAIO
  scanMode: BscanScanMode;
  surfaceCondition: string;
  nominalDiameter: string;
  nominalThicknessMm: number | null;
  technique: string;
  couplantId: string | null;
  couplant: string;
  thermometer: string;
  analysisSoftware: string;
  scannerManufacturer: string;
  technicalNotes: string;

  // 04 — EQUIPAMENTO / TRANSDUTOR / CALIBRAÇÃO
  deviceAssetId: string | null;
  deviceManufacturer: string;
  deviceModel: string;
  deviceSerialNumber: string;
  deviceCalibrationCert: string;
  deviceCalibrationValidity: string;
  transducerAssetId: string | null;
  transducerManufacturer: string;
  transducerModel: string;
  transducerFrequency: string;
  transducerAngle: string;
  transducerSerialNumber: string;
  transducerDimension: string;
  calibrationBlockAssetId: string | null;
  calibrationBlock: string;
  calibrationBlockCert: string;
  calibrationBlockCertValidity: string;

  // 05 — CRITÉRIO DE ACEITAÇÃO (norma/critério vêm do comum)
  acceptanceCode: string;
  acceptanceReference: string;
  /** Limite padrão do relatório; cada área pode ter o seu. */
  minDesignThicknessMm: number | null;
  otherTechnicalLimit: string;

  // 06/07 — ÁREAS E RESULTADOS
  scanAreas: BscanArea[];
  results: BscanResult[];

  // 08 — CROQUI / ISOMÉTRICO (a imagem é o `croqui` comum)
  drawingReference: string;
  drawingRevision: string;
  drawingDescription: string;
  drawingMarkers: BscanDrawingMarker[];

  // 10 — IMAGENS B-SCAN / C-SCAN
  scanImages: BscanImage[];

  // 11 — OBSERVAÇÕES (as gerais são `observations`, do comum)
  technicalObservations: string;

  // 12 — SIGNATÁRIOS: o INSPETOR AUTORIZADO é papel próprio do B-Scan.
  authorizedInspector: NdtSignatory | null;
}

// ──────────────────────────────────────────────────── modelo documental

/**
 * O MESMO objeto alimenta a Visualização e o PDF final. Nenhuma das três
 * saídas recalcula nada: tudo o que precisa já vem pronto e formatado.
 */
export interface BscanDocumentModel extends NdtDocumentCommon {
  title: string;
  subtitle: string;
  identificationRows: Array<[string, string]>;
  objective: string;
  technicalData: Array<[string, string]>;
  equipment: Array<[string, string]>;
  transducer: {
    manufacturer: string;
    model: string;
    frequency: string;
    angle: string;
    serialNumber: string;
    dimension: string;
  };
  acceptance: Array<[string, string]>;
  results: Array<{
    item: string;
    areaCode: string;
    identification: string;
    extent: string;
    nominalThickness: string;
    minDesignThickness: string;
    minimumThickness: string;
    laudo: BscanLaudo;
    laudoAbbreviation: string;
    observation: string;
  }>;
  drawing: {
    image: string;
    fileName: string;
    reference: string;
    caption: string;
    markers: Array<{ label: string; x: number; y: number }>;
  } | null;
  photoGroups: Array<{
    title: string;
    photos: Array<{ image: string; caption: string }>;
  }>;
  scanImages: Array<{ image: string; caption: string }>;
  generalObservations: string;
  technicalObservations: string;
  temperatureNote: string;
  bscanLegend: string;
  /**
   * As quatro colunas de "RESPONSÁVEIS", na ordem do modelo aprovado:
   * INSPETOR · APROVAÇÃO · INSPETOR AUTORIZADO · CLIENTE / FISCALIZAÇÃO.
   * Tipo próprio porque o INSPETOR AUTORIZADO não existe nos demais END e o
   * papel compartilhado (`NdtSignatoryRole`) não foi alargado por ele.
   */
  signatureColumns: Array<{
    roleLabel: string;
    name: string;
    functionTitle: string;
    credential: string;
    signatureImage: string | null;
    date: string;
  }>;
}

/** Texto da marca d'água enquanto o documento não foi aprovado pelo PLH. */

/** Situações em que o documento já está fechado e o modelo é o CONGELADO. */
export function isBscanClosed(status: string | null | undefined): boolean {
  return status === "FINALIZED" || status === "SIGNED";
}

// ──────────────────────────────────────────────────────── regras puras

const text = (value: unknown): string => (value === null || value === undefined ? "" : String(value));

/** "8,10 mm" — apresentação. Vazio quando não há número. */
export function formatMm(value: number | null | undefined): string {
  const formatted = formatDecimalBR(value ?? null);
  return formatted ? `${formatted} mm` : "";
}

/** Extensão da varredura: "500 mm" ou "500 × 300 mm". */
export function formatExtent(area: Pick<BscanArea, "lengthXMm" | "lengthYMm">): string {
  const x = area.lengthXMm;
  const y = area.lengthYMm;
  if (x === null && y === null) return "";
  const fmt = (v: number) => (Number.isInteger(v) ? String(v) : formatDecimalBR(v, 1));
  if (x !== null && y !== null) return `${fmt(x)} × ${fmt(y)} mm`;
  return `${fmt((x ?? y) as number)} mm`;
}

/** Área de um resultado, pelo vínculo — nunca pelo texto. */
export function areaOf(report: Pick<BscanReport, "scanAreas">, scanAreaKey: string | null | undefined): BscanArea | null {
  if (!scanAreaKey) return null;
  return report.scanAreas.find(a => a.key === scanAreaKey) ?? null;
}

/**
 * Limite aplicável a uma área: o da área, e na falta dele o padrão do
 * relatório. Nenhum valor é inventado — sem os dois, não há limite.
 */
export function applicableLimitMm(report: Pick<BscanReport, "minDesignThicknessMm">, area: BscanArea | null): number | null {
  return area?.minDesignThicknessMm ?? report.minDesignThicknessMm ?? null;
}

export interface BscanLaudoSuggestion {
  suggestion: BscanLaudo;
  reason: string;
}

/**
 * SUGESTÃO de laudo — nunca decisão.
 *
 * Com regra objetiva disponível (menor espessura e limite de projeto), o
 * sistema indica o que a comparação sugere. Quem decide é o inspetor: um
 * mapeamento pode exigir avaliação que a comparação simples não enxerga
 * (perda localizada, pite, extensão da região). Por isso esta função não é
 * chamada para gravar laudo — só para mostrar a sugestão ao lado do campo.
 *
 * Sem os dois números não há sugestão: inventar laudo é o que o comando
 * proíbe.
 */
export function suggestBscanLaudo(minimumThicknessMm: number | null, limitMm: number | null): BscanLaudoSuggestion {
  if (minimumThicknessMm === null || limitMm === null) {
    return { suggestion: "", reason: "Sem menor espessura e limite de projeto, não há regra objetiva para sugerir laudo." };
  }
  if (minimumThicknessMm >= limitMm) {
    return {
      suggestion: "APROVADO",
      reason: `Menor espessura ${formatMm(minimumThicknessMm)} é maior ou igual ao limite de ${formatMm(limitMm)}.`
    };
  }
  return {
    suggestion: "REPROVADO",
    reason: `Menor espessura ${formatMm(minimumThicknessMm)} está abaixo do limite de ${formatMm(limitMm)} — possível reprovação; confirme a avaliação técnica.`
  };
}

/** O próximo número livre ("01", "02"…) numa lista de itens. */
export function nextBscanItem(existing: readonly string[]): string {
  let max = 0;
  for (const value of existing) {
    const n = parseInt(String(value).replace(/[^0-9]/g, ""), 10);
    if (!Number.isNaN(n) && n > max) max = n;
  }
  return String(max + 1).padStart(2, "0");
}

/**
 * Excluir uma área apaga também o que depende dela.
 *
 * Deixar resultado, foto ou imagem apontando para uma área inexistente
 * produziria linhas órfãs no PDF, sem código de área e sem espessura
 * nominal. Resultados e imagens técnicas da área saem junto; fotos perdem o
 * vínculo mas permanecem (a vista geral do sistema continua válida).
 */
export function removeScanArea(report: BscanReport, scanAreaKey: string): BscanReport {
  return {
    ...report,
    scanAreas: report.scanAreas.filter(a => a.key !== scanAreaKey),
    results: report.results.filter(r => r.scanAreaKey !== scanAreaKey),
    scanImages: report.scanImages.filter(i => i.scanAreaKey !== scanAreaKey),
    drawingMarkers: report.drawingMarkers.filter(m => m.scanAreaKey !== scanAreaKey),
    photos: report.photos.map(p => (p.scanAreaKey === scanAreaKey ? { ...p, scanAreaKey: null } : p))
  };
}

// ─────────────────────────────────────────────────────────── resumo

export interface BscanSummary {
  areaCount: number;
  resultCount: number;
  photoCount: number;
  imageCount: number;
  globalMinimum: { thicknessMm: number; areaCode: string } | null;
  laudoCounts: Record<Exclude<BscanLaudo, "">, number>;
  pendingLaudo: number;
  norm: string;
  criteria: string;
  signatories: string[];
  pendencies: string[];
}

/** Seção 13 — o que o inspetor vê antes de finalizar. */
export function summarizeBscan(report: BscanReport): BscanSummary {
  const laudoCounts: BscanSummary["laudoCounts"] = { APROVADO: 0, REPROVADO: 0, EXAME_COMPLEMENTAR: 0, ADPC: 0 };
  let pendingLaudo = 0;
  let globalMinimum: BscanSummary["globalMinimum"] = null;
  for (const result of report.results) {
    if (result.laudo) laudoCounts[result.laudo] += 1;
    else pendingLaudo += 1;
    if (result.minimumThicknessMm !== null && (globalMinimum === null || result.minimumThicknessMm < globalMinimum.thicknessMm)) {
      globalMinimum = { thicknessMm: result.minimumThicknessMm, areaCode: areaOf(report, result.scanAreaKey)?.code ?? "" };
    }
  }
  const signatories = [
    ...report.signatories.filter(s => s.name.trim()).map(s => `${s.role}: ${s.name}`),
    ...(report.authorizedInspector?.name.trim() ? [`INSPETOR AUTORIZADO: ${report.authorizedInspector.name}`] : [])
  ];
  return {
    areaCount: report.scanAreas.length,
    resultCount: report.results.length,
    photoCount: report.photos.length,
    imageCount: report.scanImages.length,
    globalMinimum,
    laudoCounts,
    pendingLaudo,
    norm: report.referenceNorm,
    criteria: report.acceptanceCriteria,
    signatories,
    pendencies: validateBscanReportCompleteness(report).errors
  };
}

// ─────────────────────────────────────────────────────────── validação

/**
 * Pendências para FINALIZAR — cada uma dita pelo nome, nunca "dados
 * inválidos". Só entra o que é de fato obrigatório: um relatório de
 * mapeamento sem área, sem resultado ou sem aparelho não é um relatório.
 */
export function validateBscanReportCompleteness(report: BscanReport): NdtValidationResult {
  const errors = validateCommon(report, "B-Scan / C-Scan");

  if (!report.scanMode) errors.push("Selecione a modalidade do ensaio (B-Scan, C-Scan ou ambos).");
  if (!report.deviceAssetId && !report.deviceManufacturer.trim()) errors.push("Selecione o aparelho de ultrassom.");
  if (!report.deviceAssetId && !report.deviceCalibrationCert.trim()) errors.push("Informe o certificado de calibração do aparelho.");
  if (!report.transducerAssetId && !report.transducerModel.trim()) errors.push("Selecione o transdutor.");
  if (!report.calibrationBlockAssetId && !report.calibrationBlock.trim()) errors.push("Selecione o bloco de calibração.");

  if (report.scanAreas.length === 0) errors.push("Cadastre pelo menos uma área ensaiada.");
  const areasWithoutCode = report.scanAreas.filter(a => !a.code.trim()).length;
  if (areasWithoutCode > 0) errors.push(`Existem ${areasWithoutCode} ${areasWithoutCode === 1 ? "área" : "áreas"} sem código de identificação.`);

  const codes = report.scanAreas.map(a => a.code.trim().toUpperCase()).filter(Boolean);
  const duplicated = [...new Set(codes.filter((c, i) => codes.indexOf(c) !== i))];
  if (duplicated.length > 0) errors.push(`Código de área repetido: ${duplicated.join(", ")}. Cada área precisa de um código único.`);

  if (report.results.length === 0) errors.push("Registre pelo menos um resultado.");
  for (const result of report.results) {
    const area = areaOf(report, result.scanAreaKey);
    const label = area?.code || `item ${result.item}`;
    if (!area) errors.push(`O resultado ${result.item} não está vinculado a uma área ensaiada.`);
    if (result.minimumThicknessMm === null) errors.push(`Informe a menor espessura encontrada em ${label}.`);
    if (!result.laudo) errors.push(`Informe o laudo de ${label}.`);
  }

  const areasWithoutResult = report.scanAreas.filter(a => a.code.trim() && !report.results.some(r => r.scanAreaKey === a.key));
  for (const area of areasWithoutResult) errors.push(`A área ${area.code} não tem resultado registrado.`);

  return { valid: errors.length === 0, errors };
}

// ───────────────────────────────────────────────────────── normalização

const num = (value: unknown): number | null => parseDecimalBR(value as string | number | null | undefined);

/**
 * Registro gravado → forma atual. Coleções ausentes viram vazias, texto
 * ausente vira "", número volta a ser número. Nada é inventado: um valor
 * que não pode ser lido vira `null`, e a validação acusa a falta.
 */
export function normalizeBscanReport(report: BscanReport): BscanReport {
  // Mesmo padrão do USC: o registro gravado pode ter qualquer forma antiga.
  const r = report as any;
  /**
   * Os campos próprios da foto do B-Scan precisam ser guardados ANTES de
   * `normalizeCommon`: ele reescreve cada foto só com os campos comuns dos
   * END, e o vínculo com a área, o tipo, a legenda e a ordem seriam
   * descartados em toda carga do relatório.
   */
  const photoExtras = new Map<string, any>();
  for (const raw of Array.isArray(r.photos) ? r.photos : []) if (raw && raw.key) photoExtras.set(String(raw.key), raw);
  normalizeCommon(r, "BSCAN");

  for (const key of ["scanAreas", "results", "scanImages", "drawingMarkers"]) if (!Array.isArray(r[key])) r[key] = [];

  for (const key of [
    "system", "inspectionLocation", "modality", "objective", "scanMode", "surfaceCondition", "nominalDiameter",
    "technique", "couplant", "thermometer", "analysisSoftware", "scannerManufacturer", "technicalNotes",
    "deviceManufacturer", "deviceModel", "deviceSerialNumber", "deviceCalibrationCert", "deviceCalibrationValidity",
    "transducerManufacturer", "transducerModel", "transducerFrequency", "transducerAngle", "transducerSerialNumber",
    "transducerDimension", "calibrationBlock", "calibrationBlockCert", "calibrationBlockCertValidity",
    "acceptanceCode", "acceptanceReference", "otherTechnicalLimit", "drawingReference", "drawingRevision",
    "drawingDescription", "technicalObservations"
  ]) {
    if (typeof r[key] !== "string") r[key] = "";
  }
  for (const key of ["couplantId", "deviceAssetId", "transducerAssetId", "calibrationBlockAssetId"]) {
    if (r[key] === undefined) r[key] = null;
  }
  r.nominalThicknessMm = num(r.nominalThicknessMm);
  r.minDesignThicknessMm = num(r.minDesignThicknessMm);
  if (r.authorizedInspector === undefined) r.authorizedInspector = null;

  r.scanAreas = r.scanAreas.map((raw: any, index: number): BscanArea => ({
    key: text(raw.key) || `BSCAN-AREA-LEGACY-${index}`,
    item: text(raw.item) || String(index + 1).padStart(2, "0"),
    code: text(raw.code),
    identification: text(raw.identification),
    scanFile: text(raw.scanFile),
    location: text(raw.location),
    lengthXMm: num(raw.lengthXMm),
    lengthYMm: num(raw.lengthYMm),
    orientation: text(raw.orientation),
    nominalThicknessMm: num(raw.nominalThicknessMm),
    minDesignThicknessMm: num(raw.minDesignThicknessMm),
    observation: text(raw.observation)
  }));

  const validLaudos = new Set(BSCAN_LAUDO_OPTIONS.map(o => o.value));
  r.results = r.results.map((raw: any, index: number): BscanResult => ({
    key: text(raw.key) || `BSCAN-RESULT-LEGACY-${index}`,
    scanAreaKey: text(raw.scanAreaKey),
    item: text(raw.item) || String(index + 1).padStart(2, "0"),
    minimumThicknessMm: num(raw.minimumThicknessMm),
    laudo: validLaudos.has(raw.laudo) ? raw.laudo : "",
    observation: text(raw.observation)
  }));

  const validPhotoKinds = new Set(BSCAN_PHOTO_KIND_OPTIONS.map(o => o.value));
  r.photos = r.photos.map((common: any, index: number): BscanPhoto => {
    const raw = { ...(photoExtras.get(String(common.key)) ?? {}), ...common };
    const area = (r.scanAreas as BscanArea[]).find(a => a.key === raw.scanAreaKey) ?? null;
    const kind: BscanPhotoKind = validPhotoKinds.has(raw.kind) ? raw.kind : "OUTRO";
    return {
      ...raw,
      scanAreaKey: area ? area.key : null,
      kind,
      caption: text(raw.caption),
      order: Number.isFinite(Number(raw.order)) ? Number(raw.order) : index,
      // `item` é exigido pela validação comum dos END. No B-Scan ele é o
      // código da área, e na falta dela o tipo da foto — nunca vazio por
      // descuido de um campo que a tela do B-Scan não mostra.
      item: text(raw.item) || area?.code || bscanPhotoKindLabel(kind)
    };
  }).sort((a: BscanPhoto, b: BscanPhoto) => a.order - b.order);

  const validImageKinds = new Set(BSCAN_IMAGE_KIND_OPTIONS.map(o => o.value));
  r.scanImages = r.scanImages.map((raw: any, index: number): BscanImage => ({
    key: text(raw.key) || `BSCAN-IMAGE-LEGACY-${index}`,
    scanAreaKey: (r.scanAreas as BscanArea[]).some(a => a.key === raw.scanAreaKey) ? raw.scanAreaKey : null,
    kind: validImageKinds.has(raw.kind) ? raw.kind : "B_SCAN",
    image: text(raw.image),
    fileName: text(raw.fileName),
    fileSize: Number(raw.fileSize ?? 0) || 0,
    uploadedAt: text(raw.uploadedAt),
    caption: text(raw.caption),
    associatedThicknessMm: num(raw.associatedThicknessMm),
    observation: text(raw.observation),
    order: Number.isFinite(Number(raw.order)) ? Number(raw.order) : index
  })).sort((a: BscanImage, b: BscanImage) => a.order - b.order);

  r.drawingMarkers = r.drawingMarkers
    .filter((m: any) => (r.scanAreas as BscanArea[]).some(a => a.key === m.scanAreaKey))
    .map((m: any, index: number): BscanDrawingMarker => ({
      key: text(m.key) || `BSCAN-MARK-LEGACY-${index}`,
      scanAreaKey: text(m.scanAreaKey),
      x: Math.min(1, Math.max(0, Number(m.x) || 0)),
      y: Math.min(1, Math.max(0, Number(m.y) || 0))
    }));

  return report;
}
